import { formDataAdjusted } from '@/modules/contato/formData'
import {
  normalizeCNPJ,
  onlyNumbers,
  validateCNPJ,
  validateCPF,
} from '@/shared/validation/index'
import { noXssPatterns } from '@/shared/validation/xssValidation'
import { isValidNumber } from 'libphonenumber-js'
import { z } from 'zod'

const getQuestionOptions = (
  questionName: keyof typeof formDataAdjusted.questions,
): readonly [string, ...string[]] => {
  const question = formDataAdjusted.questions[questionName]

  const optionNames = question?.options
    .map((option) => option.optionName)
    .filter((optionName): optionName is string => Boolean(optionName))

  return optionNames as unknown as readonly [string, ...string[]]
}

const roleOptions = getQuestionOptions('role')

export const businessUnits = getQuestionOptions('reasonBu')

export const reasons = getQuestionOptions('reasonReason')

export const applications = getQuestionOptions('reasonApplication')

export type ContactBusinessUnit = (typeof businessUnits)[number]
export type ContactReason = (typeof reasons)[number]
export type ContactApplication = (typeof applications)[number]

export const contactFieldSchemas = {
  cpf: z
    .string()
    .min(14, { message: 'CPF deve ter 11 dígitos' })
    .max(14, { message: 'CPF deve ter 11 dígitos' })
    .transform((cpf) => onlyNumbers(cpf))
    .refine((cpf) => validateCPF(cpf), { message: 'Informe um CPF válido' }),
  fullName: z
    .string()
    .min(2, { message: 'Informe seu nome' })
    .max(200, { message: 'O nome deve ter menos de 200 caracteres' })
    .refine(
      (name) => {
        const words = name.trim().split(/\s+/)
        return words.length >= 2
      },
      { message: 'Informe seu nome completo (nome e sobrenome)' },
    )
    .refine(noXssPatterns, {
      message: 'O campo contém caracteres não permitidos',
    }),
  email: z.string().email({ message: 'Informe um email válido' }),
  emailConfirmation: z.string().email({ message: 'Informe um email válido' }),
  phone: z
    .string()
    .refine(
      (phone) => {
        const digits = onlyNumbers(phone)
        return digits.length === 10 || digits.length === 11
      },
      { message: 'O número deve ser um telefone válido' },
    )
    .refine((phone) => isValidNumber(phone, 'BR'), {
      message: 'Informe um número de telefone brasileiro válido',
    })
    .transform((phone) => onlyNumbers(phone)),
  role: z.enum(roleOptions, {
    message: 'Selecione uma das opções',
  }),
  companyName: z
    .string()
    .min(2, {
      message: 'Informe o nome da empresa em que trabalha / fornece flash',
    })
    .max(100, {
      message: 'O nome da empresa deve ter no máximo 100 caracteres',
    })
    .refine(noXssPatterns, {
      message: 'O campo contém caracteres não permitidos',
    }),
  cnpj: z
    .string()
    .transform((cnpj) => normalizeCNPJ(cnpj))
    .refine((cnpj) => cnpj.length === 14, {
      message: 'O CNPJ deve ter 14 caracteres alfanuméricos',
    })
    .refine((cnpj) => validateCNPJ(cnpj), {
      message: 'Informe um CNPJ válido',
    }),
  reasonBu: z.enum(businessUnits, {
    message: 'Selecione uma das opções',
  }),
  reasonReason: z.enum(reasons, {
    message: 'Selecione uma das opções',
  }),
  reasonApplication: z.enum(applications, {
    message: 'Selecione uma das opções',
  }),
  reasonHasNoTravelId: z.boolean().optional(),
  reasonTravelId: z.string().max(100).optional(),
  messageBody: z
    .string({
      required_error: 'Descreva como podemos ajudar',
    })
    .trim()
    .min(10, {
      message: 'Compartilhe mais detalhes para podermos ajudar melhor',
    })
    .max(2000, {
      message: 'O texto pode ter no máximo 2000 caracteres',
    })
    .refine(noXssPatterns, {
      message: 'O campo contém caracteres não permitidos',
    }),
  messageAttachments: z
    .array(z.any())
    .max(3, {
      message: 'Selecione no máximo 3 arquivos.',
    })
    .optional(),
  messageQrCode: z
    .string()
    .trim()
    .regex(/^\d*$/, {
      message: 'O QR Code deve conter apenas números',
    })
    .min(8, { message: 'O QR Code deve no mínimo 8 dígitos' })
    .max(20, { message: 'O QR Code deve ter no máximo 20 dígitos' })
    .optional(),
  messageHasNoAccount: z.boolean().optional(),
  reasonImpersonationConsent: z.boolean().optional(),
} as const satisfies Record<string, z.ZodTypeAny>

export type ContactFieldName = keyof typeof contactFieldSchemas
